import { useCallback, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { mediaUrl } from "../lib/api";

function VideoPlayer({ video, videoWebm, videoProps, soundToggle }) {
  const vref = useRef(null);
  const initialMuted = useRef(videoProps.muted !== false).current;
  const [muted, setMuted] = useState(initialMuted);
  const { muted: _ignored, ...rest } = videoProps;

  const setVideoRef = useCallback(
    (el) => {
      if (el) {
        vref.current = el;
        el.muted = initialMuted;
      }
    },
    [initialMuted]
  );

  const toggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = vref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <>
      <video ref={setVideoRef} className="w-full h-full object-cover" playsInline {...rest}>
        {videoWebm && <source src={mediaUrl(videoWebm)} type="video/webm" />}
        <source src={mediaUrl(video)} />
      </video>
      {soundToggle && (
        <button
          data-testid="hero-sound-toggle"
          onClick={toggleSound}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="absolute bottom-6 right-6 z-20 inline-flex items-center gap-2.5 border border-jm-bone/30 backdrop-blur-md bg-jm-ink/50 text-jm-bone px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] hover:bg-jm-amber hover:text-jm-ink hover:border-jm-amber transition-colors duration-300"
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          {muted ? "Son coupé" : "Son activé"}
        </button>
      )}
    </>
  );
}

export default function MediaFrame({
  label = "JM",
  sublabel = "Visuel à venir",
  accent = "#C5A059",
  image,
  video,
  videoWebm,
  videoProps = {},
  soundToggle = false,
  imagePosition = "center",
  className = "",
  children,
  testId,
}) {
  if (video) {
    return (
      <div className={`relative overflow-hidden ${className}`} data-testid={testId}>
        <VideoPlayer video={video} videoWebm={videoWebm} videoProps={videoProps} soundToggle={soundToggle} />
        <div className="absolute inset-0 jm-grain opacity-25 pointer-events-none mix-blend-overlay" />
        {children}
      </div>
    );
  }
  if (image) {
    return (
      <div className={`relative overflow-hidden ${className}`} data-testid={testId}>
        <img src={mediaUrl(image)} alt={label} className="w-full h-full object-cover" style={{ objectPosition: imagePosition }} />
        <div className="absolute inset-0 jm-grain opacity-25 pointer-events-none mix-blend-overlay" />
        {children}
      </div>
    );
  }
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      data-testid={testId}
      style={{
        background: `radial-gradient(130% 120% at 18% 8%, ${accent}2e, transparent 55%), radial-gradient(110% 100% at 88% 92%, ${accent}1c, transparent 55%), linear-gradient(158deg, #191c26 0%, #0d0e12 68%)`,
      }}
    >
      <div className="absolute inset-0 jm-grain opacity-60 pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}66, transparent)` }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
        <span className="font-serif font-light text-jm-bone/85 text-2xl sm:text-3xl tracking-[0.12em] leading-tight">
          {label}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-jm-mute">{sublabel}</span>
      </div>
      {children}
    </div>
  );
}
